import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { waLink } from "@/lib/site";
import { ChapterLabel } from "@/components/landing/Reveal";

const STEPS = [
    {
        key: "vibe",
        question: "WHAT'S THE VIBE?",
        options: ["Big Club Night", "Rooftop & Views", "Beach Club", "Lounge & Cocktails"],
    },
    {
        key: "occasion",
        question: "WHAT'S THE OCCASION?",
        options: ["Birthday", "Bachelor / Bachelorette", "Couple's Night", "Group Trip", "Just Because"],
    },
    {
        key: "budget",
        question: "HOW PREMIUM ARE WE GOING?",
        options: ["Comfortable", "Premium", "Ultra VIP"],
    },
];

const inputCls =
    "w-full rounded-sm border border-white/15 bg-[#0A0A0A] px-4 py-3.5 text-sm text-ivory transition-colors duration-300 focus:border-gold focus:outline-none";

const QuizPage = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [details, setDetails] = useState({ destination: "Bangkok", date: "", guests: 2 });

    useEffect(() => {
        document.title = "Plan Your Night in 60 Seconds | BookMyVIP";
    }, []);

    const isDetails = step >= STEPS.length;
    const current = STEPS[step];
    const canContinue = isDetails ? true : Boolean(answers[current?.key]);

    const select = (value) => {
        setAnswers((a) => ({ ...a, [current.key]: value }));
        setTimeout(() => setStep((s) => s + 1), 320);
    };

    const message = `Hi BookMyVIP, I'm planning a night in ${details.destination}. Vibe: ${answers.vibe}. Occasion: ${answers.occasion}. Budget: ${answers.budget}. Date: ${details.date || "flexible"}. Guests: ${details.guests}. Please share VIP table options.`;

    const restart = () => {
        setAnswers({});
        setDetails({ destination: "Bangkok", date: "", guests: 2 });
        setStep(0);
    };

    return (
        <main
            data-testid="quiz-page"
            className="mx-auto flex min-h-[100svh] max-w-3xl flex-col px-5 pb-32 pt-32 md:px-10 md:pt-40"
        >
            <ChapterLabel number="Nº 09" title="Night Builder" testId="quiz-label" />

            <div className="mt-10 flex items-center gap-4">
                <span
                    data-testid="quiz-progress-label"
                    className="font-serif text-lg italic text-gold"
                >
                    {String(Math.min(step + 1, 4)).padStart(2, "0")} / 04
                </span>
                <div className="h-px flex-1 bg-white/10">
                    <motion.div
                        className="h-px bg-gold"
                        animate={{ width: `${(Math.min(step, 3) / 3) * 100}%` }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    />
                </div>
            </div>

            <AnimatePresence mode="wait">
                {!isDetails ? (
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <h1
                            data-testid="quiz-question"
                            className="mt-10 font-serif text-3xl font-medium tracking-tight text-ivory md:text-5xl"
                        >
                            {current.question}
                        </h1>
                        <div className="mt-10 grid gap-4 sm:grid-cols-2">
                            {current.options.map((opt, i) => (
                                <button
                                    key={opt}
                                    data-testid={`quiz-option-${current.key}-${i}`}
                                    onClick={() => select(opt)}
                                    className={`group rounded-sm border p-6 text-left transition-colors duration-300 ${
                                        answers[current.key] === opt
                                            ? "border-gold bg-gold/10"
                                            : "border-white/10 bg-[#0F0F0F] hover:border-gold/50"
                                    }`}
                                >
                                    <span
                                        className={`font-serif text-xl italic transition-colors duration-300 md:text-2xl ${
                                            answers[current.key] === opt
                                                ? "text-gold"
                                                : "text-ivory group-hover:text-gold"
                                        }`}
                                    >
                                        {opt}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {step > 0 && (
                            <button
                                data-testid="quiz-back-btn"
                                onClick={() => setStep((s) => s - 1)}
                                className="mt-10 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-gold"
                            >
                                <ArrowLeft size={14} strokeWidth={1.5} /> Back
                            </button>
                        )}
                    </motion.div>
                ) : (
                    <motion.div
                        key="details"
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <h1 className="mt-10 font-serif text-3xl font-medium tracking-tight text-ivory md:text-5xl">
                            LAST DETAILS,{" "}
                            <span className="italic text-gold">THEN WE SEND IT.</span>
                        </h1>

                        <div className="mt-8 flex flex-wrap gap-3">
                            {[answers.vibe, answers.occasion, answers.budget].map((a) => (
                                <span
                                    key={a}
                                    className="border border-gold/30 bg-gold/5 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-gold"
                                >
                                    {a}
                                </span>
                            ))}
                        </div>

                        <div className="mt-10 grid gap-5 sm:grid-cols-3">
                            <div>
                                <label htmlFor="quiz-destination" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
                                    Destination
                                </label>
                                <select
                                    id="quiz-destination"
                                    data-testid="quiz-destination-select"
                                    value={details.destination}
                                    onChange={(e) => setDetails((d) => ({ ...d, destination: e.target.value }))}
                                    className={inputCls}
                                >
                                    {["Bangkok", "Phuket", "Pattaya", "Koh Samui", "Not sure yet"].map((d) => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="quiz-date" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
                                    Date
                                </label>
                                <input
                                    id="quiz-date"
                                    data-testid="quiz-date-input"
                                    type="date"
                                    value={details.date}
                                    onChange={(e) => setDetails((d) => ({ ...d, date: e.target.value }))}
                                    className={`${inputCls} [color-scheme:dark]`}
                                />
                            </div>
                            <div>
                                <label htmlFor="quiz-guests" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
                                    Guests
                                </label>
                                <input
                                    id="quiz-guests"
                                    data-testid="quiz-guests-input"
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={details.guests}
                                    onChange={(e) => setDetails((d) => ({ ...d, guests: e.target.value }))}
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        <div
                            data-testid="quiz-message-preview"
                            className="mt-10 rounded-sm border border-white/10 bg-[#0A0A0A] p-6"
                        >
                            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted">
                                Your concierge message
                            </p>
                            <p className="mt-3 font-serif text-lg italic leading-relaxed text-ivory/80">
                                "{message}"
                            </p>
                        </div>

                        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
                            <a
                                data-testid="quiz-send-whatsapp-btn"
                                href={waLink(message)}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Opens WhatsApp"
                                className="group inline-flex items-center justify-center gap-3 rounded-sm bg-gold px-8 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-soft"
                            >
                                Send on WhatsApp
                                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                            </a>
                            <button
                                data-testid="quiz-restart-btn"
                                onClick={restart}
                                className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/25 px-8 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
                            >
                                <RotateCcw size={13} strokeWidth={1.5} /> Start Over
                            </button>
                        </div>

                        <button
                            data-testid="quiz-back-btn-details"
                            onClick={() => setStep(STEPS.length - 1)}
                            className="mt-10 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-gold"
                        >
                            <ArrowLeft size={14} strokeWidth={1.5} /> Back
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
};

export default QuizPage;
